import { createSelector } from "reselect";
import { isEmpty } from "lodash";
import { isRiverrDomain, shouldHidePaymentMethodRequirement } from "../utils";
import { USER_TO_BYPASS_ALERTS } from "../constants/users";
import { isTestEnv } from "../helpers/common";
import { enterpriseHasFeature } from "../helpers/enterpriseFeatures";
import { shouldSkipSaaSPricingUi } from "../helpers/saasPricingGate";

const currentUserRecord = (state) => state?.currentUser?.user;
const currentEnterpriseRecord = (state) => state?.currentUser?.enterprise;
const paymentMethodsRecord = (state) => state?.credentials?.paymentMethods;
const paymentMethodsLoadingRecord = (state) => state?.credentials?.loading;
const planIdRecord = (state) => state?.pricings?.planId;

export const showAddCardDetailsAlertSelector = createSelector(
  [
    currentUserRecord,
    currentEnterpriseRecord,
    paymentMethodsRecord,
    paymentMethodsLoadingRecord,
    planIdRecord,
  ],
  (
    currentUser,
    currentEnterprise,
    paymentMethods,
    paymentMethodsLoading,
    planId
  ) => {
    if (USER_TO_BYPASS_ALERTS.includes(currentUser?.email)) {
      return false;
    }

    if (shouldHidePaymentMethodRequirement(currentEnterprise, currentUser)) {
      return false;
    }

    if (currentUser?.billingDisabled) return false;

    if (paymentMethodsLoading) return false;
    if (!enterpriseHasFeature(currentEnterprise, "paid_members"))
      return false;
    if (!currentUser?.shopifyUser) return false;
    if (!planId) return false;
    return isEmpty(paymentMethods);
  }
);

export const noPaymentMethodsAvailableSelector = createSelector(
  [
    currentUserRecord,
    currentEnterpriseRecord,
    paymentMethodsRecord,
    paymentMethodsLoadingRecord,
  ],
  (currentUser, currentEnterprise, paymentMethods, paymentMethodsLoading) => {
    if (paymentMethodsLoading || isRiverrDomain) {
      return false;
    }

    if (shouldHidePaymentMethodRequirement(currentEnterprise, currentUser)) {
      return false;
    }

    // Admin Billing toggle: invoices are manual, so no card on file.
    if (currentUser?.billingDisabled) {
      return false;
    }

    // check payment methods only for enterprise admins
    if (
      currentUser?.isEnterpriseAdmin &&
      currentUser?.uid !== currentEnterprise?.uid
    ) {
      return false;
    }

    if (USER_TO_BYPASS_ALERTS.includes(currentUser?.email)) {
      return false;
    }

    if (enterpriseHasFeature(currentEnterprise, "paid_members")) {
      return false;
    }

    // if (currentUser?.shopifyUser) {
    //   return false;
    // }

    if (isTestEnv) {
      return false;
    }

    if (currentUser?.isProUser) {
      return isEmpty(paymentMethods);
    }

    if (!currentUser?.isEnterpriseAdmin) {
      return isEmpty(paymentMethods);
    }

    return false;
  }
);

/** Ops-granted trial end time in ms (Firestore Timestamp JSON or epoch). */
const opsTrialEndsAtMs = (opsTrial) => {
  const endsAt = opsTrial?.endsAt;
  if (!endsAt) return null;
  if (typeof endsAt === "number") return endsAt > 1e12 ? endsAt : endsAt * 1000;
  const seconds = endsAt._seconds ?? endsAt.seconds;
  if (seconds != null) return Number(seconds) * 1000;
  return null;
};

export const isLockedToPricingSelector = createSelector(
  [
    currentUserRecord,
    currentEnterpriseRecord,
    paymentMethodsRecord,
    paymentMethodsLoadingRecord,
    planIdRecord,
  ],
  (
    currentUser,
    currentEnterprise,
    paymentMethods,
    paymentMethodsLoading,
    planId
  ) => {
    if (paymentMethodsLoading) return false;
    if (shouldSkipSaaSPricingUi(currentUser, currentEnterprise)) return false;
    if (!currentUser?.isEnterpriseAdmin) return false;

    // Ops-granted enterprise trial (e.g. Fabritech): after expiry, force
    // /pricing until they pick a paid/free plan via the normal upgrade path
    // (which clears enterprise.opsTrial).
    const opsTrial = currentEnterprise?.opsTrial;
    if (opsTrial?.lockToPricingAfterExpiry) {
      const endsMs = opsTrialEndsAtMs(opsTrial);
      const expired =
        !!opsTrial.expired || (endsMs != null && Date.now() >= endsMs);
      if (expired) return true;
    }

    // Free plan threshold crossed — hard-lock to Pricing until they pick a
    // higher plan + add a card (planLimitLock cleared on paid upgrade).
    if (currentEnterprise?.planLimitLock?.active) return true;

    if (currentUser?.signupSource !== "accounts.riverr.app") return false;
    // Lock until they have both a payment method and a plan
    return isEmpty(paymentMethods) || !planId;
  }
);

export const isMemberSubscribedSelector = createSelector(
  [currentUserRecord, currentEnterpriseRecord],
  (currentUser, currentEnterprise) => {
    if (currentUser?.isEnterpriseAdmin) {
      return true;
    }

    if (shouldHidePaymentMethodRequirement(currentEnterprise, currentUser)) {
      return true;
    }

    if (USER_TO_BYPASS_ALERTS.includes(currentUser?.email)) {
      return true;
    }

    if (enterpriseHasFeature(currentEnterprise, "paid_members")) {
      if (currentUser?.planId) {
        return true;
      }

      return false;
    }

    return true;
  }
);
